import * as React from 'react';
import {Query} from 'react-apollo';
// @ts-expect-error
import type {DataProxy} from 'react-apollo';
import {Helmet} from 'react-helmet';

// @ts-expect-error
import memoize from 'memoize-one';

import {translateConnectionObjectToArray} from 'nutshell-core';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {headTitle} from 'shells/document/head-title';
import type {DateHeader} from 'shells/email-row-item/types';
import {EmptyStateLarge} from 'shells/empty-states/empty-state-large';

import type {
    GetFailedEmails as GetFailedEmailsQuery,
    GetFailedEmailsVariables as GetFailedEmailsQueryVariables,
    Email as EmailFragment,
    UpdateEmail as UpdateEmailMutation,
    DeleteEmail as DeleteEmailMutation,
    RetryFailedEmail as RetryFailedEmailMutation,
} from 'nutshell-graphql-types';

import {EmailControlBar} from '../email-control-bar';
import {EmailList} from '../email-list';
import GET_FAILED_EMAILS from '../graphql/queries/get-failed-emails.graphql';
import {addHeaders, ERROR_STATE} from '../utils';

import {FailedEmailRowItem} from './failed-email-row-item';

import styles from '../email-list.module.css';

const LIMIT = 50;

type Props = {
    isLoading: boolean;
    emails: EmailFragment[];
    hasNextPage: boolean;
    totalCount: number;
    loadNextPage: () => Promise<any>;
    errorMessage: string | null | undefined;
};

export class FailedMailComponent extends React.PureComponent<Props> {
    addHeadersToEmails: (arg1: EmailFragment[]) => EmailFragment | DateHeader[];

    constructor() {
        // @ts-expect-error
        super();
        // @ts-expect-error
        this.addHeadersToEmails = memoize((emails) => addHeaders(emails));
    }

    render() {
        const emails = this.addHeadersToEmails(this.props.emails);

        const failedEmptyState = (
            <EmptyStateLarge
                title='Nothing but clear skies'
                subtitle='All your emails have sent successfully. If there’s ever a problem sending one of your emails, it’ll show up here, and you can try sending it again.'
                icon={
                    <img
                        alt=''
                        src='/include/images/email-marketing/email_failed_empty_state.png'
                        className={styles['large-icon']}
                    />
                }
            />
        );

        return (
            <React.Fragment>
                <Helmet>
                    <title>{headTitle('Failed emails')}</title>
                </Helmet>
                <EmailControlBar
                    totalCount={this.props.totalCount}
                    // @ts-expect-error
                    onAllFailedEmailsDeleted={updateCacheFromDeleteAll}
                />
                <EmailList
                    isRequesting={this.props.isLoading}
                    onFetchMoreRows={this.props.loadNextPage}
                    hasNextPage={this.props.hasNextPage}
                    // @ts-expect-error
                    collection={emails}
                    specialStates={[
                        {
                            shouldRender: () => Boolean(this.props.errorMessage),
                            component: ERROR_STATE,
                        },
                        {
                            // @ts-expect-error
                            shouldRender: () => emails.length === 0 && !this.props.isLoading,
                            component: failedEmptyState,
                        },
                    ]}
                    renderRow={(emailItem: EmailFragment, isFirstOfDate: boolean) => {
                        return (
                            <FailedEmailRowItem
                                key={emailItem.id}
                                email={emailItem}
                                isFirstOfDate={isFirstOfDate}
                                onEmailSharedChange={updateCacheFromUpdate}
                                onEmailDeleted={updateCacheFromDelete}
                                onEmailRetried={updateCacheFromRetry}
                            />
                        );
                    }}
                />
            </React.Fragment>
        );
    }
}

// @ts-expect-error
function updateCacheFromDeleteAll(proxy, mutationResult) {
    if (!mutationResult || !mutationResult.data || !mutationResult.data.failedEmailsDelete) return;

    // Read the data from our cache for this query.
    const data: GetFailedEmailsQuery | null | undefined = proxy.readQuery({
        query: GET_FAILED_EMAILS,
        variables: {
            limit: LIMIT,
        },
    });

    if (!data) return;

    // It would be better to get the new set from the mutation result, but we only get a count
    // and instead of changing it I'm (@ianvs) just changing the count
    data.failedEmails.totalCount =
        data.failedEmails.totalCount - mutationResult.data.failedEmailsDelete.numEmailsDeleted;
    if (data.failedEmails.totalCount === 0) {
        data.failedEmails.edges = [];
    }

    // Write our data back to the cache.
    proxy.writeQuery({
        query: GET_FAILED_EMAILS,
        variables: {
            limit: LIMIT,
        },
        data,
    });
}

function updateCacheFromRetry(
    proxy: DataProxy,
    mutationResult: {
        data?: RetryFailedEmailMutation;
    }
) {
    const retriedEmailId = mutationResult.data && mutationResult.data.failedEmailRetry.email.id;

    if (!retriedEmailId) return;

    // Read the data from our cache for this query.
    const data: GetFailedEmailsQuery | null | undefined = proxy.readQuery({
        query: GET_FAILED_EMAILS,
        variables: {
            limit: LIMIT,
        },
    });

    if (!data) return;

    // Remove deleted items from cache
    data.failedEmails.edges = data.failedEmails.edges.filter(
        (edge) => edge.node && edge.node.id && edge.node.id !== retriedEmailId
    );

    data.failedEmails.totalCount = data.failedEmails.edges.length;

    // Write our data back to the cache.
    proxy.writeQuery({
        query: GET_FAILED_EMAILS,
        variables: {
            limit: LIMIT,
        },
        data,
    });
}

function updateCacheFromDelete(
    proxy: DataProxy,
    mutationResult: {
        data?: DeleteEmailMutation;
    }
) {
    const deletedEmailId = mutationResult.data && mutationResult.data.emailDelete.email.id;

    if (!deletedEmailId) return;

    // Read the data from our cache for this query.
    const cacheData: GetFailedEmailsQuery | null | undefined = proxy.readQuery({
        query: GET_FAILED_EMAILS,
        variables: {
            limit: LIMIT,
        },
    });

    if (!cacheData) return;

    // Remove deleted items from cache
    cacheData.failedEmails.edges = cacheData.failedEmails.edges.filter(
        (edge) => edge.node && edge.node.id && edge.node.id !== deletedEmailId
    );

    cacheData.failedEmails.totalCount = cacheData.failedEmails.edges.length;

    // Write our data back to the cache.
    proxy.writeQuery({
        query: GET_FAILED_EMAILS,
        variables: {
            limit: LIMIT,
        },
        data: cacheData,
    });
}

function updateCacheFromUpdate(
    proxy: DataProxy,
    mutationResult: {
        data?: UpdateEmailMutation;
    }
) {
    const emailShareAndCreateContacts =
        mutationResult.data && mutationResult.data.emailShareAndCreateContacts;

    if (!emailShareAndCreateContacts) return;

    const updatedEmailId = emailShareAndCreateContacts.email.id;

    // Read the data from our cache for this query.
    const data: GetFailedEmailsQuery | null | undefined = proxy.readQuery({
        query: GET_FAILED_EMAILS,
        variables: {
            limit: LIMIT,
        },
    });

    if (!data) return;

    // Update our new email item
    data.failedEmails.edges = data.failedEmails.edges.map((edge) => {
        if (edge.node && edge.node.id && edge.node.id === updatedEmailId) {
            return {
                __typename: 'EmailsEdge',
                node: {...edge.node, isShared: emailShareAndCreateContacts.email.isShared},
                cursor: edge.cursor,
            };
        }

        return edge;
    });

    // Write our data back to the cache.
    proxy.writeQuery({
        query: GET_FAILED_EMAILS,
        variables: {
            limit: LIMIT,
        },
        data,
    });
}

class FailedEmailQuery extends Query<GetFailedEmailsQuery, GetFailedEmailsQueryVariables> {}

export const FailedMail = () => (
    <FailedEmailQuery
        query={GET_FAILED_EMAILS}
        notifyOnNetworkStatusChange={true}
        variables={{limit: LIMIT}}
    >
        {({data, loading, error, networkStatus, fetchMore}) => {
            const failedEmails = data ? data.failedEmails : null;

            return (
                <FailedMailComponent
                    isLoading={loading}
                    // @ts-expect-error
                    errorMessage={safelyGetGraphQLErrorMessage(error)}
                    // @ts-expect-error
                    networkStatus={networkStatus}
                    totalCount={failedEmails ? failedEmails.totalCount : 0}
                    hasNextPage={failedEmails ? failedEmails.pageInfo.hasNextPage : true}
                    emails={failedEmails ? translateConnectionObjectToArray(failedEmails) : []}
                    loadNextPage={() =>
                        fetchMore({
                            variables: {
                                limit: LIMIT,
                                cursor: failedEmails ? failedEmails.pageInfo.endCursor : null,
                            },
                            updateQuery: (previousResult, fetchResult) => {
                                const fetchMoreResult = fetchResult.fetchMoreResult;
                                if (!fetchMoreResult) return previousResult;

                                const previousEdges = previousResult.failedEmails.edges;
                                const newEdges = fetchMoreResult.failedEmails.edges;
                                const newTotalCount = fetchMoreResult.failedEmails.totalCount;
                                const newPageInfo = fetchMoreResult.failedEmails.pageInfo;

                                return {
                                    // Put the new emails at the end of the list and update `pageInfo`
                                    // so we have the new `endCursor` and `hasNextPage` values
                                    failedEmails: {
                                        __typename: 'EmailsConnection',
                                        totalCount: newTotalCount,
                                        pageInfo: newPageInfo,
                                        edges: [...previousEdges, ...newEdges],
                                    },
                                };
                            },
                        })
                    }
                />
            );
        }}
    </FailedEmailQuery>
);
